import { selectWidgetQuery } from "./../state/selectors/tank-profile.selectors";
import { HistoricalDeliveryEfficiencyChartComponent } from "./../widget/historical-delivery-efficiency-chart/historical-delivery-efficiency-chart.component";
import {
  KeyDatesChartVM,
  KeyDateSeries,
} from "./../interfaces/keydateschartresponse";
import { map, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { DSRESTClient } from "../shared/communication-objects/restclient.service";
import { api, part } from "../shared/communication-objects/api-path";
import { Observable, of } from "rxjs";
import {
  KeyDatesChartResponse,
  KeyDateTypes,
} from "../interfaces/keydateschartresponse";
import { Cacheable } from "ts-cacheable";
import { HttpParams } from "@angular/common/http";
import { ITankAggregatedUsageChartVM } from '../interfaces/tankAggregatedUsageChart';
import { DateTime } from "@syncfusion/ej2-angular-charts";

export const ChartColors = {
  yellow: "#FDB933",
  red: "#c33e4b",
  green: "#54af25",
  blue: "blue",
};

@Injectable()
export class DashboardWidgetService {
  private readonly apiUrl = api`dashboardwidget`;

  constructor(private restClient: DSRESTClient) {}

  @Cacheable()
  getAggregateDataAsync(params: any, columnName: string) {
    //get observerable aggregate widget data from the web api
    if (params)
      return this.restClient.post(
        api`${this.apiUrl}/getaggregatedata/${part(columnName.toLowerCase())}`,
        params
      );
  }

  @Cacheable()
  getVolumeAndAlarmDataAsync(params: any, alarms: number[]): Observable<any> {
    if (params)
      return this.restClient.post(
        api`${this.apiUrl}/getvolumeandalarrmdata?${new HttpParams().set("alarms", alarms.join())}`,
        params
      );
  }

  getAggregateData(rows: any, columnName: string) {
    class chartData {
      name: string;
      count: number = 0;
      volume: number = 0;

      constructor(name: string) {
        this.name = name;
      }
    }

    let aggregateItems = new Map<string, chartData>();

    // loop thru rows and accumulate
    let rowCount: number = 0;

    rows.forEach((row) => {
      rowCount += 1;
      let name = row[columnName].trim();
      if (name === "") name = "Other";

      // see if aggregated item is already in the map, and add it if it is not
      let aggregateData = aggregateItems.get(name);
      if (aggregateData === undefined) {
        aggregateData = new chartData(name);
        aggregateItems.set(name, aggregateData);
      }
      aggregateData.count += 1;
      aggregateData.volume = aggregateData.volume + row.netVolume;
    });

    // sort map by volume in descending order
    let values = Array.from(aggregateItems.values());
    values.sort((a, b) => {
      return a.volume > b.volume ? -1 : 1;
    });

    let resultCount = aggregateItems.size;

    // if > maxRowCount rows, consolidate all remaining rows into "Other"
    let maxRowCount = 11;
    if (resultCount > maxRowCount) {
      // see if we already have 'Other', else use maxRowCount row
      let otherRow: number = values.findIndex((x) => x.name === "Other");
      if (otherRow === -1) {
        otherRow = maxRowCount - 1;
        values[otherRow].name = "Other";
      }

      while (resultCount > maxRowCount) {
        values[otherRow].volume += values[resultCount - 1].volume;
        resultCount -= 1;
      }
      values = values.slice(0, maxRowCount);
    }

    return values;
  }

  @Cacheable()
  getDaysToKeyDates(params: any): Observable<KeyDatesChartVM[] | null> {
    return this.restClient
      .post(api`${this.apiUrl}/getkeydateschartdata`, params)
      .pipe(
        map((response) => {
          let keyDatesResponse: KeyDatesChartResponse = response;
          const todaysDate = keyDatesResponse.trendLineData.find(
            (data) => data.keyDateType === KeyDateTypes.TodaysDate
          )?.date;

          let x = keyDatesResponse.trendLineData.map((dataPoint) => {
            return {
              date: dataPoint.date,
              volume: dataPoint.volume,
              color:
                dataPoint.date < todaysDate
                  ? ChartColors.yellow
                  : ChartColors.red,
              dashArray: this.getKeyToKeyDatesDashArray(dataPoint.keyDateType),
              legendText: this.getKeyToKeyDatesLegendText(
                dataPoint.keyDateType
              ),
              striplineColor:
                dataPoint.date < todaysDate
                  ? ChartColors.yellow
                  : dataPoint.date === todaysDate
                  ? ChartColors.green
                  : ChartColors.red,
              keyDateType: dataPoint.keyDateType,
            };
          });

          return x;
        })
      );
  }

  @Cacheable()
  getHistoricalDeliveryEfficiencies(
    params: any
  ): Observable<HistoricalDeliveryEfficiencyChartDTO | null> {
    return this.restClient
      .post(
        api`${this.apiUrl}/get-historical-delivery-efficency-chart-data`,
        params,
      )
      .pipe(
        map((response: HistoricalDeliveryEfficiencyChartDTO) => {
          if (!response) {
            const empty: HistoricalDeliveryEfficiencyChartDTO = {
              targetDeliveryEfficiency: 0,
              dataPoints: [],
            };
            return empty;
          }

          response.dataPoints.forEach((dataPoint) => {
            dataPoint.monthName = `${dataPoint.monthName} ${dataPoint.year}`;
          });

          return response;
        }),
      );
  }

  
  @Cacheable()
  getCompareHistoricalDeliveryEfficiencies(
    params: any
  ): Observable<HistoricalDeliveryEfficiencyChartDTO | null> {
    return this.restClient
      .post(
        api`${this.apiUrl}/get-historical-delivery-efficency-compare-chart-data`,
        params,
      )
      .pipe(
        map((response: HistoricalDeliveryEfficiencyChartDTO) => {
          if (!response) {
            const empty: HistoricalDeliveryEfficiencyChartDTO = {
              targetDeliveryEfficiency: 0,
              dataPoints: [],
            };
            return empty;
          }

          response.dataPoints.forEach((dataPoint) => {
            dataPoint.monthName = `${dataPoint.monthName} ${dataPoint.year}`;
          });

          return response;
        }),
      );
  }

  @Cacheable()
  getHistoricalDeliveryEfficienciesForTanks(
    params: any
  ): Observable<ComparePopupResponseDTO | null> {
    return this.restClient
      .post(
        api`${this.apiUrl}/get-historical-delivery-efficency-tanks-data`,
        params,
      )
      .pipe(
        map((response: ComparePopupResponseDTO) => {
          if (!response) {
            const noData: ComparePopupResponseDTO = {
              targetDeliveryEfficiency: 0,
              chartData: [],
              gridRows: [],
              months: []
            };

            return noData;
          }
          response.chartData.forEach(dp => {
            dp.monthName = `${dp.monthName} ${dp.year}`;
          });

          return response;
        }),
      );
  }


  @Cacheable()
  getUsageAndDeliveryData(
    params: any
  ): Observable<ITankAggregatedUsageChartVM | null> {
    return this.restClient
      .post(
        api`${this.apiUrl}/get-usage-delivery-chart-data`,
        params,
      )
      .pipe(
        map((response: ITankAggregatedUsageChartVM) => {
          return response;
        }),
      );
  }

  getKeyDateUISettings(): {
    keyDateType: number;
    dashArray: string;
    legendText: string;
    color: string;
  }[] {
    return [
      {
        keyDateType: KeyDateTypes.DateToOrder,
        dashArray: "5,8",
        color: ChartColors.yellow,
        legendText: "Charting.Legend.DatetoOrder",
      },
      {
        keyDateType: KeyDateTypes.DateToSafetyStock,
        dashArray: "5,1",
        color: ChartColors.red,
        legendText: "Charting.Legend.DatetoSafetyStock",
      },
      {
        keyDateType: KeyDateTypes.DateToEmpty,
        dashArray: "5,3",
        color: ChartColors.red,
        legendText: "Charting.Legend.DatetoEmpty",
      },
      {
        keyDateType: KeyDateTypes.TodaysDate,
        dashArray: null,
        color: ChartColors.green,
        legendText: "Charting.Legend.TodaysDate",
      },
    ];
  }


  private getKeyToKeyDatesLegendText(keyDateType?: KeyDateTypes): any {
    switch (keyDateType) {
      case KeyDateTypes.DateToOrder:
        return "Charting.Legend.DatetoOrder";
      case KeyDateTypes.DateToSafetyStock:
        return "Charting.Legend.DatetoSafetyStock";
      case KeyDateTypes.DateToEmpty:
        return "Charting.Legend.DatetoEmpty";
      case KeyDateTypes.TodaysDate:
        return "Charting.Legend.TodaysDate";
      default:
        return null;
    }
  }

  private getKeyToKeyDatesDashArray(keyDateType?: KeyDateTypes): any {
    switch (keyDateType) {
      case KeyDateTypes.DateToOrder:
        return "5,1";
      case KeyDateTypes.DateToSafetyStock:
        return "5,3";
      case KeyDateTypes.DateToEmpty:
        return "5,8";
      case KeyDateTypes.TodaysDate:
      default:
        return null;
    }
  }
}

export interface HistoricalDeliveryEfficiencyChartDTO {
  targetDeliveryEfficiency: number;
  dataPoints: HistoricalDeliveryEfficiencyDataPoint[];
}

export interface HistoricalDeliveryEfficiencyDataPoint {
  year: number;
  monthName: string;
  monthNo: number;
  averageDeliveryEfficiency: number;
  filterValue: string;
  date: Date;
}

export interface ComparePopupResponseDTO {
  targetDeliveryEfficiency: number;
  chartData: HistoricalDeliveryEfficiencyDataPoint[];
  gridRows: TankMonthlyDeliveryDTO[];
  months: string[];
}

export interface TankMonthlyDeliveryDTO {
  tankId: number;
  tankName: string;
  rtuNumber: number;
  prodDesc: string;
  monthlyData: { [key: string]: number | null };
}